import {DropdownVATOptions, getVatPercentageForDropdownVAT} from "../../../../../../types/enums.ts";
import {DEFAULT_INVOICE_NUMBER_PREFIX, InvoiceLineItem, vatOptionLabel} from "../types.ts";

export function roundToCents(amount: number): number {
    return Math.round(amount * 100) / 100
}

export function vatPercentageForBackend(vat: DropdownVATOptions): number | null {
    return getVatPercentageForDropdownVAT(vat)
}

export function effectiveVatRate(vat: DropdownVATOptions): number {
    return vatPercentageForBackend(vat) ?? 0
}

export function isLineFilled(line: InvoiceLineItem): boolean {
    return line.description.trim() !== '' || (line.unitPrice !== null && line.unitPrice !== 0)
}

export function lineTotal(line: InvoiceLineItem): number {
    return roundToCents((line.quantity ?? 0) * (line.unitPrice ?? 0))
}

export type VatBreakdownEntry = {
    vat: DropdownVATOptions
    base: number
    amount: number
}

export type InvoiceTotals = {
    totalExclVat: number
    vatBreakdown: VatBreakdownEntry[]
    totalVat: number
    totalInclVat: number
}

export function computeInvoiceTotals(lines: InvoiceLineItem[]): InvoiceTotals {
    const filled = lines.filter(isLineFilled)
    const totalExclVat = roundToCents(filled.reduce((sum, line) => sum + lineTotal(line), 0))

    const basePerVat = new Map<DropdownVATOptions, number>()
    for (const line of filled) {
        basePerVat.set(line.vat, roundToCents((basePerVat.get(line.vat) ?? 0) + lineTotal(line)))
    }

    const vatBreakdown: VatBreakdownEntry[] = []
    for (const [vat, base] of basePerVat.entries()) {
        if (base === 0) continue
        vatBreakdown.push({vat, base, amount: roundToCents(base * effectiveVatRate(vat) / 100)})
    }

    const totalVat = roundToCents(vatBreakdown.reduce((sum, entry) => sum + entry.amount, 0))
    return {
        totalExclVat,
        vatBreakdown,
        totalVat,
        totalInclVat: roundToCents(totalExclVat + totalVat),
    }
}

export function yearFromISODate(isoDate: string): number {
    const match = /^(\d{4})-/.exec(isoDate)
    return match ? Number(match[1]) : new Date().getFullYear()
}

export function formatInvoiceNumber(prefix: string, year: number, sequence: number): string {
    return `${prefix}-${year}-${String(sequence).padStart(5, '0')}`
}

export function formatDutchInvoiceDate(isoDate: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate)
    return match ? `${match[3]}-${match[2]}-${match[1]}` : ''
}

export type InvoiceNumberingSnapshot = {
    year: number
    prefix: string
    next_sequence: number
}

export type InvoiceNumberPreview = {
    effectivePrefix: string
    isNumberingCurrent: boolean
    nextSequence: number | null
    numberPreview: string
}

export function resolveInvoiceNumberPreview(
    invoiceDate: string,
    prefix: string,
    numbering: InvoiceNumberingSnapshot | null,
): InvoiceNumberPreview {
    const year = yearFromISODate(invoiceDate)
    const effectivePrefix = prefix.trim() || numbering?.prefix || DEFAULT_INVOICE_NUMBER_PREFIX
    const isNumberingCurrent =
        numbering !== null && numbering.year === year && numbering.prefix === effectivePrefix

    return {
        effectivePrefix,
        isNumberingCurrent,
        nextSequence: isNumberingCurrent ? numbering.next_sequence : null,
        numberPreview: isNumberingCurrent
            ? formatInvoiceNumber(effectivePrefix, year, numbering.next_sequence)
            : `${effectivePrefix}-${year}-#####`,
    }
}

export function pdfVatLabel(vat: DropdownVATOptions): string {
    return vatOptionLabel(vat).toLowerCase()
}

export function describeVatBreakdownEntry(entry: VatBreakdownEntry): string {
    return `Btw (${pdfVatLabel(entry.vat)})`
}

export function formatQuantity(quantity: number): string {
    return String(quantity).replace('.', ',')
}
